import { useEffect, useState, type FormEvent } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Layout from '@/components/Layout'
import { fetchPart, createPart, updatePart, fetchVendors } from '@/lib/api'
import { CONDITIONS, CATEGORIES, type Part, type Vendor } from '@/lib/types'

function slugify(s: string) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
}

export default function PartForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = !!id

  const [vendors, setVendors] = useState<Vendor[]>([])
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState({
    title: '', slug: '', brand: '', model: '', year: '', category: 'Engine',
    oem: '', condition: 'Used' as Part['condition'], price_aed: '',
    description: '', photo: '', vendor_id: '', featured: false, is_active: true,
  })

  useEffect(() => {
    fetchVendors().then(setVendors).catch(() => {})
    if (isEdit) {
      fetchPart(id!).then(p => {
        if (p) {
          setForm({
            title: p.title, slug: p.slug, brand: p.brand, model: p.model ?? '', year: p.year ?? '',
            category: p.category, oem: p.oem ?? '', condition: p.condition,
            price_aed: String(p.price_aed), description: p.description ?? '', photo: p.photo ?? '',
            vendor_id: p.vendor_id ?? '', featured: p.featured, is_active: p.is_active,
          })
        }
        setLoading(false)
      }).catch(() => setLoading(false))
    }
  }, [id, isEdit])

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm(f => ({ ...f, [key]: value }))
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    setSaving(true)
    try {
      const payload = {
        title: form.title,
        slug: form.slug || slugify(form.title),
        brand: form.brand,
        model: form.model || null,
        year: form.year || null,
        category: form.category,
        oem: form.oem || null,
        condition: form.condition,
        price_aed: Number(form.price_aed),
        description: form.description || null,
        photo: form.photo || null,
        vendor_id: form.vendor_id || null,
        featured: form.featured,
        is_active: form.is_active,
      }
      if (isEdit) {
        await updatePart(id!, payload)
      } else {
        await createPart(payload)
      }
      navigate('/parts')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save part')
    } finally {
      setSaving(false)
    }
  }

  const input = 'w-full px-3 py-2.5 rounded-xl border border-surface-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent'
  const label = 'block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5'

  return (
    <Layout title={isEdit ? 'Edit Part' : 'Add Part'}>
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl border border-surface-200 shadow-card p-6 md:p-8">
          {loading ? (
            <p className="text-slate-400 text-sm py-8 text-center">Loading...</p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className={label}>Title *</label>
                  <input className={input} value={form.title} onChange={e => set('title', e.target.value)} required
                    placeholder="e.g. Mercedes E-Class W211 M272 3.5L V6 Engine" />
                </div>
                <div>
                  <label className={label}>Slug</label>
                  <input className={input} value={form.slug} onChange={e => set('slug', slugify(e.target.value))} placeholder="auto-generated from title" />
                </div>
                <div>
                  <label className={label}>Vendor *</label>
                  <select className={input} value={form.vendor_id} onChange={e => set('vendor_id', e.target.value)} required>
                    <option value="">Select vendor...</option>
                    {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={label}>Brand *</label>
                  <input className={input} value={form.brand} onChange={e => set('brand', e.target.value)} required placeholder="Mercedes" />
                </div>
                <div>
                  <label className={label}>Model</label>
                  <input className={input} value={form.model} onChange={e => set('model', e.target.value)} placeholder="E280 / E350 (W211)" />
                </div>
                <div>
                  <label className={label}>Year</label>
                  <input className={input} value={form.year} onChange={e => set('year', e.target.value)} placeholder="2003–2009" />
                </div>
                <div>
                  <label className={label}>Category</label>
                  <select className={input} value={form.category} onChange={e => set('category', e.target.value)}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className={label}>OEM / Part No</label>
                  <input className={input} value={form.oem} onChange={e => set('oem', e.target.value)} placeholder="272 963 30 00" />
                </div>
                <div>
                  <label className={label}>Condition</label>
                  <select className={input} value={form.condition} onChange={e => set('condition', e.target.value as Part['condition'])}>
                    {CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className={label}>Price (AED) *</label>
                  <input type="number" min="0" step="0.01" className={input} value={form.price_aed}
                    onChange={e => set('price_aed', e.target.value)} required placeholder="4500" />
                </div>
                <div className="md:col-span-2">
                  <label className={label}>Photo URL</label>
                  <input className={input} value={form.photo} onChange={e => set('photo', e.target.value)}
                    placeholder="/images/parts/mercedes-w211-m272-engine.jpg or https://..." />
                  {form.photo && (
                    <img src={form.photo} alt="preview" className="mt-3 h-32 rounded-xl object-cover border border-surface-200"
                      onError={e => { (e.target as HTMLImageElement).style.opacity = '0.2' }} />
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className={label}>Description</label>
                  <textarea rows={4} className={input} value={form.description} onChange={e => set('description', e.target.value)}
                    placeholder="Complete M272 V6 engine, tested and compression-checked..." />
                </div>
              </div>

              <div className="flex items-center gap-6 pt-2">
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={form.featured} onChange={e => set('featured', e.target.checked)}
                    className="w-4 h-4 rounded border-surface-300 text-brand-600 focus:ring-brand-500" />
                  Featured
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)}
                    className="w-4 h-4 rounded border-surface-300 text-brand-600 focus:ring-brand-500" />
                  Active (visible)
                </label>
              </div>

              <div className="flex gap-3 pt-4 border-t border-surface-100">
                <button type="submit" disabled={saving}
                  className="bg-brand-600 text-white font-semibold text-sm px-6 py-2.5 rounded-xl hover:bg-brand-700 disabled:opacity-50 transition-colors">
                  {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Part'}
                </button>
                <button type="button" onClick={() => navigate('/parts')}
                  className="bg-white border border-surface-200 text-slate-600 font-semibold text-sm px-6 py-2.5 rounded-xl hover:bg-surface-50 transition-colors">
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </Layout>
  )
}
